import React, { useState, useRef, useEffect } from 'react';
import { apiCall } from '../utils/api';
import { toast } from './ui/sonner';
import { 
  MessageCircle, X, Send, Bot, User, ChevronDown, ChevronRight,
  Phone, Mail, Clock, HelpCircle, FileText, AlertCircle, CheckCircle,
  Loader2, ArrowLeft, ExternalLink, Copy
} from 'lucide-react';

// FAQ Data
const FAQ_CATEGORIES = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: '🚀',
    faqs: [
      {
        question: 'How do I create an account?',
        answer: 'To create an account, click "Sign Up" on the homepage, select your role (Worker, Employer, or Agent), fill in your details, set up a security question for account recovery, and verify your email address.'
      },
      {
        question: 'How do I complete my profile?',
        answer: 'After logging in, go to your Dashboard and click on the "Profile" tab. Fill in your personal information, upload a profile photo, add your skills and certifications, and set your hourly rate and availability.'
      },
      {
        question: 'What documents do I need to get started?',
        answer: 'Workers need to upload valid ID, relevant certifications (if any), and complete a background check. Employers need to provide business verification documents. The specific requirements depend on your industry and location.'
      }
    ]
  },
  {
    id: 'jobs-shifts',
    title: 'Jobs & Shifts',
    icon: '💼',
    faqs: [
      {
        question: 'How do I find available jobs?',
        answer: 'Go to the "Find Jobs" tab in your Worker Dashboard. You can filter jobs by location, pay rate, shift time, and required skills. Enable location services to see jobs near you.'
      },
      {
        question: 'How do I accept a shift?',
        answer: 'When you find a suitable job, click "Accept Shift" on the job card. You\'ll receive a confirmation and the shift will appear in your "My Shifts" tab. Make sure to check in on time using GPS verification.'
      },
      {
        question: 'Can I cancel a shift after accepting?',
        answer: 'You can cancel a shift up to 24 hours before the start time without penalty. Cancellations within 24 hours may affect your reliability rating. Contact the employer or support if you have an emergency.'
      },
      {
        question: 'What happens if I\'m running late?',
        answer: 'Contact the employer immediately through the in-app messaging system. Frequent lateness can affect your rating and future job opportunities.'
      }
    ]
  },
  {
    id: 'payments',
    title: 'Payments & Wallet',
    icon: '💰',
    faqs: [
      {
        question: 'How do I get paid?',
        answer: 'After completing a shift and checking out, your earnings are added to your wallet within 24-48 hours after employer approval. You can then withdraw funds to your linked bank account or payment method.'
      },
      {
        question: 'What payment methods are available?',
        answer: 'We support direct bank transfers, PayPal, and instant pay options (where available). Go to Wallet > Payment Methods to set up your preferred withdrawal method.'
      },
      {
        question: 'Why is my payment pending?',
        answer: 'Payments may be pending while the employer reviews your timesheet or if there are any discrepancies. Most payments clear within 48 hours. Contact support if your payment is pending for more than 3 business days.'
      },
      {
        question: 'Are there any fees?',
        answer: 'Standard withdrawals are free. Instant pay options may have a small fee (typically 1-2%). Check the Wallet section for current fee information.'
      }
    ]
  },
  {
    id: 'certificates',
    title: 'Certificates & Verification',
    icon: '📜',
    faqs: [
      {
        question: 'How do I upload certificates?',
        answer: 'Go to Profile > Certificates and click "Add Certificate". Upload a photo or PDF of your certificate, fill in the details including expiry date, and submit for verification.'
      },
      {
        question: 'How long does certificate verification take?',
        answer: 'Automatic verification (for supported certificate types) takes a few minutes. Manual verification may take 1-3 business days. You\'ll receive a notification once verification is complete.'
      },
      {
        question: 'What if my certificate is expiring soon?',
        answer: 'You\'ll receive automatic reminders 30 days before expiry. Make sure to renew your certificates on time and upload the new documents to maintain your verification status.'
      }
    ]
  },
  {
    id: 'account',
    title: 'Account & Security',
    icon: '🔐',
    faqs: [
      {
        question: 'How do I reset my password?',
        answer: 'Click "Forgot Password" on the login page, enter your email, and either answer your security question or enter the OTP sent to your email. Then create a new password.'
      },
      {
        question: 'How do I change my email or phone number?',
        answer: 'Go to Profile > Settings and update your contact information. You may need to verify the new email or phone number before the change takes effect.'
      },
      {
        question: 'How do I delete my account?',
        answer: 'Contact our support team to request account deletion. Please note that this action is permanent and you\'ll lose access to your earnings history and reviews.'
      }
    ]
  },
  {
    id: 'employers',
    title: 'For Employers',
    icon: '🏢',
    faqs: [
      {
        question: 'How do I post a job?',
        answer: 'Go to your Employer Dashboard and click "Post New Job". Fill in the job details including title, description, required skills, pay rate, and shift times. Your job will be visible to qualified workers immediately.'
      },
      {
        question: 'How do I pay workers?',
        answer: 'After workers complete their shifts, review and approve their timesheets in the "Timesheets" section. Approved timesheets are processed automatically and workers are paid from your account balance.'
      },
      {
        question: 'How do I leave a review for a worker?',
        answer: 'After a shift is completed, you\'ll be prompted to rate the worker. You can also leave reviews anytime from the shift history in your dashboard.'
      }
    ]
  }
];

export default function LiveChatSupport({ isOpen, onClose }) {
  const [activeView, setActiveView] = useState('menu'); // menu, faq, chat, contact
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm your ScensobConnect support assistant. I can help you with questions about jobs, payments, certificates, and more. How can I help you today?"
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showEscalateOption, setShowEscalateOption] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await apiCall('/api/support/ai-chat', {
        method: 'POST',
        body: JSON.stringify({
          message: userMessage,
          conversation_history: messages.slice(-10)
        })
      });

      setMessages(prev => [...prev, { role: 'assistant', content: response.response }]);
      
      if (response.suggest_escalation) {
        setShowEscalateOption(true);
      }
    } catch (error) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I'm having trouble connecting right now. Please try again or contact our support team directly."
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEscalateToLive = async () => {
    setIsLoading(true);
    try {
      await apiCall('/api/support/tickets', {
        method: 'POST',
        body: JSON.stringify({
          subject: 'Live Support Request',
          description: 'User requested live support from AI chat',
          conversation_history: messages,
          priority: 'high'
        })
      });

      setMessages(prev => [...prev, { 
        role: 'system', 
        content: "✅ Your request has been submitted. A support agent will contact you shortly via email or in-app message. Our typical response time is within 2 hours during business hours."
      }]);
      setShowEscalateOption(false);
      toast.success('Support ticket created! We\'ll be in touch soon.');
    } catch (error) {
      toast.error('Failed to create support ticket. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSupport = () => {
    const subject = encodeURIComponent('Support Request - ScensobConnect');
    const body = encodeURIComponent('Hi ScensobConnect Support Team,\n\nI need help with:\n\n[Please describe your issue here]\n\nThank you!');
    window.location.href = `mailto:support@scensobconnect.com?subject=${subject}&body=${body}`;
    toast.success('Opening email client...');
  };

  const handleCallSupport = () => {
    window.location.href = 'tel:+18007246762';
    toast.success('Opening phone dialer...');
  };

  const copyPhoneNumber = () => {
    navigator.clipboard.writeText('1-800-724-6762');
    toast.success('Phone number copied!');
  };

  const copyEmail = () => {
    navigator.clipboard.writeText('support@scensobconnect.com');
    toast.success('Email copied!');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl h-[80vh] max-h-[700px] shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="flex items-center gap-3">
            {activeView !== 'menu' && (
              <button 
                onClick={() => {
                  if (activeView === 'faq' && selectedCategory) {
                    setSelectedCategory(null);
                  } else {
                    setActiveView('menu');
                    setSelectedCategory(null);
                  }
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
            )}
            <div className="p-2 bg-white/20 rounded-xl">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Help & Support</h3>
              <p className="text-sm text-white/80">
                {activeView === 'menu' && 'How can we help?'}
                {activeView === 'faq' && (selectedCategory ? selectedCategory.title : 'Frequently Asked Questions')}
                {activeView === 'chat' && 'Chat with AI Assistant'}
                {activeView === 'contact' && 'Contact Us'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Main Menu */}
          {activeView === 'menu' && (
            <div className="p-6 space-y-4">
              <button
                onClick={() => setActiveView('chat')}
                className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 rounded-xl transition-all group"
              >
                <div className="p-3 bg-blue-500 rounded-xl text-white">
                  <Bot className="w-6 h-6" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-semibold text-gray-900">Chat with AI Assistant</p>
                  <p className="text-sm text-gray-600">Get instant answers to your questions</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={() => setActiveView('faq')}
                className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 rounded-xl transition-all group"
              >
                <div className="p-3 bg-purple-500 rounded-xl text-white">
                  <FileText className="w-6 h-6" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-semibold text-gray-900">Browse FAQs</p>
                  <p className="text-sm text-gray-600">Find answers to common questions</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
              </button>

              <button
                onClick={() => setActiveView('contact')}
                className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 rounded-xl transition-all group"
              >
                <div className="p-3 bg-green-500 rounded-xl text-white">
                  <Phone className="w-6 h-6" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-semibold text-gray-900">Contact Support</p>
                  <p className="text-sm text-gray-600">Email, call, or request a callback</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
              </button>

              {/* Quick Info */}
              <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Support Hours</span>
                </div>
                <p className="text-sm text-gray-600">Monday - Friday: 8AM - 8PM EST</p>
                <p className="text-sm text-gray-600">Saturday - Sunday: 9AM - 5PM EST</p>
              </div>
            </div>
          )}

          {/* FAQ View */}
          {activeView === 'faq' && !selectedCategory && (
            <div className="p-6 space-y-3">
              {FAQ_CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category)}
                  className="w-full flex items-center gap-4 p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group"
                >
                  <span className="text-2xl">{category.icon}</span>
                  <div className="flex-1 text-left">
                    <p className="font-semibold text-gray-900">{category.title}</p>
                    <p className="text-sm text-gray-500">{category.faqs.length} questions</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
                </button>
              ))}
            </div>
          )}

          {/* FAQ Category Detail */}
          {activeView === 'faq' && selectedCategory && (
            <div className="p-6 space-y-3">
              {selectedCategory.faqs.map((faq, index) => (
                <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 bg-white hover:bg-gray-50 transition-colors text-left"
                  >
                    <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
                    <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${expandedFaq === index ? 'rotate-180' : ''}`} />
                  </button>
                  {expandedFaq === index && (
                    <div className="p-4 bg-gray-50 border-t border-gray-200">
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}

              <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                <p className="text-sm text-blue-800">
                  <strong>Still have questions?</strong> Chat with our AI assistant or contact support directly.
                </p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => setActiveView('chat')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                  >
                    Chat Now
                  </button>
                  <button
                    onClick={() => setActiveView('contact')}
                    className="px-4 py-2 bg-white text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors border border-blue-200"
                  >
                    Contact Us
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Chat View */}
          {activeView === 'chat' && (
            <div className="flex flex-col h-full">
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((msg, index) => (
                  <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`flex items-start gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                      <div className={`p-2 rounded-full flex-shrink-0 ${
                        msg.role === 'user' ? 'bg-blue-600' : msg.role === 'system' ? 'bg-green-500' : 'bg-purple-600'
                      }`}>
                        {msg.role === 'user' ? (
                          <User className="w-4 h-4 text-white" />
                        ) : msg.role === 'system' ? (
                          <CheckCircle className="w-4 h-4 text-white" />
                        ) : (
                          <Bot className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div className={`p-3 rounded-2xl ${
                        msg.role === 'user' 
                          ? 'bg-blue-600 text-white rounded-br-md' 
                          : msg.role === 'system'
                          ? 'bg-green-50 text-green-800 border border-green-200 rounded-bl-md'
                          : 'bg-gray-100 text-gray-800 rounded-bl-md'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-full bg-purple-600">
                        <Bot className="w-4 h-4 text-white" />
                      </div>
                      <div className="p-3 bg-gray-100 rounded-2xl rounded-bl-md">
                        <Loader2 className="w-5 h-5 text-gray-500 animate-spin" />
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Escalate Option */}
              {showEscalateOption && (
                <div className="mx-4 mb-2 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm text-amber-800">Need more help? Connect with a live support agent.</p>
                      <button
                        onClick={handleEscalateToLive}
                        disabled={isLoading}
                        className="mt-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors disabled:opacity-50"
                      >
                        Request Live Support
                      </button>
                    </div>
                    <button onClick={() => setShowEscalateOption(false)} className="text-amber-600 hover:text-amber-800">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* Input */}
              <div className="p-4 border-t border-gray-200">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Type your message..."
                    className="flex-1 p-3 bg-gray-100 rounded-xl text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isLoading}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={isLoading || !inputMessage.trim()}
                    className="p-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Contact View */}
          {activeView === 'contact' && (
            <div className="p-6 space-y-6">
              {/* Email Support */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-500 rounded-xl">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">Email Support</h4>
                    <p className="text-sm text-gray-600 mb-3">Get a response within 24 hours</p>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-blue-600 font-medium">support@scensobconnect.com</span>
                      <button onClick={copyEmail} className="p-1 hover:bg-blue-200 rounded transition-colors">
                        <Copy className="w-4 h-4 text-blue-600" />
                      </button>
                    </div>
                    <button
                      onClick={handleEmailSupport}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Mail className="w-5 h-5" />
                      Send Email
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Phone Support */}
              <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-500 rounded-xl">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">Phone Support</h4>
                    <p className="text-sm text-gray-600 mb-3">Speak directly with our team</p>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-green-600 font-medium text-lg">1-800-SCENSOB</span>
                      <span className="text-gray-500">(1-800-724-6762)</span>
                      <button onClick={copyPhoneNumber} className="p-1 hover:bg-green-200 rounded transition-colors">
                        <Copy className="w-4 h-4 text-green-600" />
                      </button>
                    </div>
                    <button
                      onClick={handleCallSupport}
                      className="w-full px-4 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Phone className="w-5 h-5" />
                      Call Now
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Request Callback */}
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-purple-500 rounded-xl">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">Request Callback</h4>
                    <p className="text-sm text-gray-600 mb-3">We'll call you back at your preferred time</p>
                    <button
                      onClick={handleEscalateToLive}
                      disabled={isLoading}
                      className="w-full px-4 py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <MessageCircle className="w-5 h-5" />
                          Request Callback
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Support Hours */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-gray-500" />
                  <h4 className="font-semibold text-gray-900">Support Hours</h4>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium text-gray-700">Weekdays</p>
                    <p className="text-gray-600">8:00 AM - 8:00 PM EST</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Weekends</p>
                    <p className="text-gray-600">9:00 AM - 5:00 PM EST</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  * Emergency support available 24/7 for urgent shift issues
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
